/**
 * §9.1 — which surface a person works in, and where they land after signing in.
 *
 * The contract's two layers:
 *
 * > **Role tiles** — four large, touch-friendly tiles … Plain login still works exactly as today. The tiles
 * > pre-select the post-login surface, **they never gate authentication**. Backend roles stay the single
 * > source of truth; if the account's real role differs from the chosen tile, follow the real role and say
 * > so in a toast.
 *
 * > **Working-surface selection** … remembered per device, switchable in-app without re-login.
 *
 * This module is the rule; the login screen, the shell and the switcher read it. It lives in
 * `@agos/shared` because three different places need the same answer and §5 forbids a module owning it.
 *
 * **Nothing here decides who may do what.** The tile is a preference; the server's `systemRole` and the
 * permission matrix are the authority, and a surface a role does not have simply is not offered.
 */

export type RoleTile = 'waiter' | 'driver' | 'kitchen' | 'admin'

/** What the tiles are called, and which system role each expects. */
export const ROLE_TILES: readonly { tile: RoleTile; role: string }[] = [
  { tile: 'waiter', role: 'waiter' },
  { tile: 'driver', role: 'driver' },
  { tile: 'kitchen', role: 'cook' },
  { tile: 'admin', role: 'admin' },
]

export function tileForRole(role: string | undefined): RoleTile {
  if (role === 'waiter') return 'waiter'
  if (role === 'driver') return 'driver'
  if (role === 'cook') return 'kitchen'
  return 'admin'
}

/**
 * Whether the account that signed in is the one the tile expected.
 *
 * §9.1: *"if the account's real role differs from the chosen tile, follow the real role and say so in a
 * toast."* `main_admin` matches the admin tile as well as `admin` does — they are the same surface.
 */
export function tileMatchesRole(tile: RoleTile, role: string | undefined): boolean {
  if (role === undefined) return false
  if (tile === 'admin') return role === 'admin' || role === 'main_admin'
  return tileForRole(role) === tile
}

// ─── The surfaces themselves ─────────────────────────────────────────────────

export type Surface = 'app' | 'driving' | 'board'

export interface SurfaceOption {
  surface: Surface
  path: string
}

/**
 * The surfaces a role may work in, in the order they are offered, **default first**.
 *
 * §9.1's defaults: *"kitchen → nástenka, driver → driving surface, waiter → its channel-scoped app view,
 * admin → `/dashboard`."*
 *
 * A `main_admin` or `admin` gets every surface, because they are the people who have to look at what their
 * staff see. Everybody else gets their own and the plain app.
 */
export function surfacesFor(role: string | undefined): SurfaceOption[] {
  const app: SurfaceOption = { surface: 'app', path: '/dashboard' }
  const driving: SurfaceOption = { surface: 'driving', path: '/driving' }
  const board: SurfaceOption = { surface: 'board', path: '/kitchen' }

  if (role === 'driver') return [driving, app]
  if (role === 'cook') return [board, app]
  // A waiter's default is the app; §9.1 asks for it scoped to their sales channels, which is §9.5's picker
  // and not built yet. Until it is, they get the app unscoped rather than a scoping that does nothing.
  if (role === 'waiter') return [app]
  if (role === 'admin' || role === 'main_admin') return [app, driving, board]
  return [app]
}

export function defaultSurface(role: string | undefined): Surface {
  return surfacesFor(role)[0]?.surface ?? 'app'
}

export function surfaceIsAvailable(surface: Surface, role: string | undefined): boolean {
  return surfacesFor(role).some((option) => option.surface === surface)
}

export function pathForSurface(surface: Surface, role: string | undefined): string {
  const match = surfacesFor(role).find((option) => option.surface === surface)
  return match?.path ?? '/dashboard'
}

// ─── Remembered per device ───────────────────────────────────────────────────

/**
 * `fk_surface` — the chosen surface, per device.
 *
 * §9.1 says *remembered per device*, so `localStorage` and not the profile: the same driver on the office
 * desktop and on their phone wants different answers, and `preferredLanguage` already showed what happens
 * when a per-device choice is stored per account.
 */
export const SURFACE_STORAGE_KEY = 'fk_surface'

function read(storage: Pick<Storage, 'getItem'>): string | null {
  try {
    return storage.getItem(SURFACE_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * The surface to open, given what was remembered and who actually signed in.
 *
 * A remembered surface the role does not have is **discarded, not honoured** — somebody demoted from driver
 * to waiter must not keep landing on the driving board, and a device shared between two people must not
 * hand the second one the first one's screen.
 */
export function resolveSurface(
  role: string | undefined,
  storage: Pick<Storage, 'getItem'> | null,
): Surface {
  const remembered = storage === null ? null : read(storage)
  if (remembered !== null && surfaceIsAvailable(remembered as Surface, role)) {
    return remembered as Surface
  }
  return defaultSurface(role)
}

export function rememberSurface(surface: Surface, storage: Pick<Storage, 'setItem'>): void {
  try {
    storage.setItem(SURFACE_STORAGE_KEY, surface)
  } catch {
    // A kiosk with storage disabled still works for this session; the default is applied next time.
  }
}

export function forgetSurface(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(SURFACE_STORAGE_KEY)
  } catch {
    // nothing to forget
  }
}

/**
 * Where a sign-in lands.
 *
 * The tile is a hint. If the account's real role disagrees with it the real role wins, and the caller is
 * told so it can say why — which is exactly what §9.1 asks for.
 */
export interface LandingDecision {
  path: string
  surface: Surface
  /** Set when the tile and the account disagreed, so the caller can explain the redirect. */
  tileIgnored: RoleTile | null
}

export function landingFor(
  role: string | undefined,
  tile: RoleTile | null,
  storage: Pick<Storage, 'getItem'> | null,
): LandingDecision {
  const surface = resolveSurface(role, storage)
  return {
    surface,
    path: pathForSurface(surface, role),
    tileIgnored: tile !== null && !tileMatchesRole(tile, role) ? tile : null,
  }
}
